<script setup lang="ts">
import { useI18n } from "vue-i18n";
import Accordion from "primevue/accordion";
import Button from "primevue/button";
import { trainerDetails } from "../main";

const { t } = useI18n();
const trainer = trainerDetails;

const openConsultationLink = () => {
    globalThis.open(t('accessibility.consultation.url'), '_blank', 'noopener,noreferrer');
};
</script>

<template>
    <MainMenu highlight="privacy" :trainer="trainer" />
    <div class="app-wrapper mb-8">
        <div id="main-content" tabindex="-1" class="header-section mt-8">
            <!-- Consultation button - shown above title on mobile -->
            <div class="consultation-button-mobile md:hidden mt-2">
                <Button
                    class="consultation-btn p-4 font-bold"
                    icon="pi pi-external-link"
                    icon-pos="right"
                    :label="t('accessibility.consultation.arrangeConsultation')"
                    :aria-label="t('accessibility.consultation.arrangeConsultation') + ' ' + t('accessibility.links.externalLink')"
                    @click="openConsultationLink"
                />
            </div>

            <!-- Mobile layout: Title only (button shown above) -->
            <div class="md:hidden">
                <TitleHeadline :title="t('data.privacy.heading')" :subtitle="t('data.privacy.title')" />
            </div>

            <!-- Desktop layout: Title and button side by side -->
            <div class="hidden md:flex md:justify-between md:gap-12">
                <div class="title-container">
                    <TitleHeadline
                        :title="t('data.privacy.heading')"
                        :subtitle="t('data.privacy.title')"
                        :top-margin="0"
                    />
                </div>
                <Button
                    class="mt-auto mb-auto ml-auto p-4 font-bold"
                    icon="pi pi-external-link"
                    icon-pos="right"
                    :label="t('accessibility.consultation.arrangeConsultation')"
                    :aria-label="t('accessibility.consultation.arrangeConsultation') + ' ' + t('accessibility.links.externalLink')"
                    @click="openConsultationLink"
                />
            </div>
        </div>
        <p v-html="t('data.privacy.introduction')"></p>
        <Accordion :active-index="0">
            <AccordionPanel value="0">
                <AccordionHeader>{{ t('data.privacy.responsibility.heading') }}</AccordionHeader>
                <AccordionContent>
                    <p class="m-0" v-html="t('data.privacy.responsibility.content')"></p>
                </AccordionContent>
            </AccordionPanel>
            <AccordionPanel value="1">
                <AccordionHeader>{{ t('data.privacy.location.heading') }}</AccordionHeader>
                <AccordionContent>
                    <p class="m-0" v-html="t('data.privacy.location.content')"></p>
                </AccordionContent>
            </AccordionPanel>
            <AccordionPanel value="2">
                <AccordionHeader>{{ t('data.privacy.transparency.heading') }}</AccordionHeader>
                <AccordionContent>
                    <p class="m-0" v-html="t('data.privacy.transparency.content')"></p>
                </AccordionContent>
            </AccordionPanel>
            <AccordionPanel value="3">
                <AccordionHeader>{{ t('data.privacy.leak.heading') }}</AccordionHeader>
                <AccordionContent>
                    <p class="m-0" v-html="t('data.privacy.leak.content')"></p>
                </AccordionContent>
            </AccordionPanel>
            <AccordionPanel value="4">
                <AccordionHeader>{{ t('data.privacy.questions.heading') }}</AccordionHeader>
                <AccordionContent>
                    <p class="m-0" v-html="t('data.privacy.questions.content')"></p>
                </AccordionContent>
            </AccordionPanel>
            <AccordionPanel value="5">
                <AccordionHeader>{{ t('data.privacy.revoke.heading') }}</AccordionHeader>
                <AccordionContent>
                    <p class="m-0" v-html="t('data.privacy.revoke.content')"></p>
                </AccordionContent>
            </AccordionPanel>
            <AccordionPanel value="6" style="--p-accordion-header-background: var(--primary-color); --p-accordion-header-color: #ffffff; --p-accordion-header-toggle-icon-color: #ffffff;">
                <AccordionHeader>{{ t('dpa.title') }}</AccordionHeader>
                <AccordionContent>
                    <TitleHeadline :title="t('dpa.title')" :subtitle="t('dpa.heading')" />
                    <p class="m-0" v-html="t('dpa.introduction')"></p>
                    <h3>{{ t('dpa.heading-1.title') }}</h3>
                    <h4 v-html="t('dpa.heading-1.subheading-1.title')"></h4>
                    <p v-html="t('dpa.heading-1.subheading-1.content')"></p>
                    <h5 v-html="t('dpa.heading-1.subheading-1.subheading-1.title')"></h5>
                    <p v-html="t('dpa.heading-1.subheading-1.subheading-1.content')"></p>
                    <h5 v-html="t('dpa.heading-1.subheading-1.subheading-2.title')"></h5>
                    <p v-html="t('dpa.heading-1.subheading-1.subheading-2.content')"></p>
                    <h5 v-html="t('dpa.heading-1.subheading-1.subheading-3.title')"></h5>
                    <p v-html="t('dpa.heading-1.subheading-1.subheading-3.content')"></p>
                    <h5 v-html="t('dpa.heading-1.subheading-1.subheading-4.title')"></h5>
                    <p v-html="t('dpa.heading-1.subheading-1.subheading-4.content')"></p>
                    <h5 v-html="t('dpa.heading-1.subheading-1.subheading-5.title')"></h5>
                    <p v-html="t('dpa.heading-1.subheading-1.subheading-5.content')"></p>
                    <h5 v-html="t('dpa.heading-1.subheading-1.subheading-6.title')"></h5>
                    <p v-html="t('dpa.heading-1.subheading-1.subheading-6.content')"></p>
                    <h5 v-html="t('dpa.heading-1.subheading-1.subheading-7.title')"></h5>
                    <p v-html="t('dpa.heading-1.subheading-1.subheading-7.content')"></p>
                    <h5 v-html="t('dpa.heading-1.subheading-1.subheading-8.title')"></h5>
                    <p v-html="t('dpa.heading-1.subheading-1.subheading-8.content')"></p>
                    <h5 v-html="t('dpa.heading-1.subheading-1.subheading-9.title')"></h5>
                    <p v-html="t('dpa.heading-1.subheading-1.subheading-9.content')"></p>
                    <h4 v-html="t('dpa.heading-1.subheading-2.title')"></h4>
                    <h5 v-html="t('dpa.heading-1.subheading-2.subheading-1.title')"></h5>
                    <p v-html="t('dpa.heading-1.subheading-2.subheading-1.content')"></p>
                    <h5 v-html="t('dpa.heading-1.subheading-2.subheading-2.title')"></h5>
                    <p v-html="t('dpa.heading-1.subheading-2.subheading-2.content')"></p>
                    <h5 v-html="t('dpa.heading-1.subheading-2.subheading-3.title')"></h5>
                    <p v-html="t('dpa.heading-1.subheading-2.subheading-3.content')"></p>
                    <h5 v-html="t('dpa.heading-1.subheading-2.subheading-4.title')"></h5>
                    <p v-html="t('dpa.heading-1.subheading-2.subheading-4.content')"></p>
                    <h4 v-html="t('dpa.heading-1.subheading-3.title')"></h4>
                    <p v-html="t('dpa.heading-1.subheading-3.content')"></p>
                    <h4 v-html="t('dpa.heading-1.subheading-4.title')"></h4>
                    <p v-html="t('dpa.heading-1.subheading-4.content')"></p>
                    <h4 v-html="t('dpa.heading-1.subheading-5.title')"></h4>
                    <p v-html="t('dpa.heading-1.subheading-5.content')"></p>
                    <h4 v-html="t('dpa.heading-1.subheading-6.title')"></h4>
                    <p v-html="t('dpa.heading-1.subheading-6.content')"></p>
                    <h4 v-html="t('dpa.heading-1.subheading-7.title')"></h4>
                    <h5 v-html="t('dpa.heading-1.subheading-7.subheading-1.title')"></h5>
                    <p v-html="t('dpa.heading-1.subheading-7.subheading-1.content')"></p>
                    <h5 v-html="t('dpa.heading-1.subheading-7.subheading-2.title')"></h5>
                    <p v-html="t('dpa.heading-1.subheading-7.subheading-2.content')"></p>
                    <h5 v-html="t('dpa.heading-1.subheading-7.subheading-3.title')"></h5>
                    <p v-html="t('dpa.heading-1.subheading-7.subheading-3.content')"></p>
                    <h5 v-html="t('dpa.heading-1.subheading-7.subheading-objection.title')"></h5>
                    <p v-html="t('dpa.heading-1.subheading-7.subheading-objection.content')"></p>
                    <h5 v-html="t('dpa.heading-1.subheading-7.subheading-encryption.title')"></h5>
                    <p v-html="t('dpa.heading-1.subheading-7.subheading-encryption.content')"></p>
                    <h5 v-html="t('dpa.heading-1.subheading-7.subheading-4.title')"></h5>
                    <p v-html="t('dpa.heading-1.subheading-7.subheading-4.content')"></p>
                    <h5 v-html="t('dpa.heading-1.subheading-7.subheading-5.title')"></h5>
                    <p v-html="t('dpa.heading-1.subheading-7.subheading-5.content')"></p>
                </AccordionContent>
            </AccordionPanel>
        </Accordion>
    </div>
    <Footer />
</template>

<style scoped>
.consultation-button-mobile {
    text-align: center;
}

/* Square corners now come from the shared theme preset (@persolog/theme), so no override is needed. */
.app-wrapper :deep(.p-accordion) {
    --p-accordion-header-active-background: var(--primary-color);
    --p-accordion-header-hover-background: var(--primary-color);
    --p-accordion-header-active-hover-background: var(--primary-color);
    --p-accordion-header-active-color: #ffffff;
    --p-accordion-header-hover-color: #ffffff;
    --p-accordion-header-active-hover-color: #ffffff;
    --p-accordion-header-toggle-icon-hover-color: #ffffff;
    --p-accordion-header-toggle-icon-active-color: #ffffff;
    --p-accordion-header-toggle-icon-active-hover-color: #ffffff;
    --p-accordion-content-padding: 1rem;
}
</style>
